/**
 * Tickets list page — /tickets
 *
 * Phase 02: List view. Phase 05: Statistical tab added.
 * Server-paginated via useTickets / useUnassignedTickets with encodeTicketFilters.
 * Filters wired: status, priority, type_group, source, assignees (tech/cs/sale),
 * date range (requested_at), security_tag. Rejected by staging: marjorable,
 * response_late — excluded (see phase-02 report).
 *
 * Phase 05: Wraps list + statistics in Tabs. Date-range filter (start_date /
 * end_date) lives outside tabs and drives all statistical charts.
 * Local date build uses getFullYear/getMonth/getDate (not toISOString) to avoid
 * UTC shift in Asia/Saigon (ui-conventions §4).
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type FilterCondition, type FilterField } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useTickets, useUnassignedTickets, exportTickets, type TicketListParams } from '@/api/tickets';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TICKET_COLUMNS, TICKET_TOGGLEABLE_COLUMN_IDS } from './list-configs/ticket-column-definitions';
import {
  TICKET_STATUS_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
  TICKET_TYPE_GROUP_OPTIONS,
  TICKET_SOURCE_OPTIONS,
  TICKET_SECURITY_TAG_OPTIONS,
  TICKET_RESPONSE_LATE_OPTIONS,
  TICKET_MARJORABLE_OPTIONS,
} from './list-configs/ticket-filter-constants';
import { TicketListToolbar } from './components/ticket-list-toolbar';
import { TicketBulkAssignDialog } from './components/ticket-bulk-assign-dialog';
import { TicketStatisticsTab } from './statistics/ticket-statistics-tab';
import type { StatDateParams } from '@/api/tickets-statistics';
import { DateRangeInput } from '@/components/date-input';

const PAGE_SIZE = 20;

/** Build a local YYYY-MM-DD string without UTC shift (ui-conventions §4). */
function toLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultStartDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return toLocalDate(d);
}

function defaultEndDate(): string {
  return toLocalDate(new Date());
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * FilterField[] wired to backend-accepted filter keys (probe-verified).
 *
 * Assignee ID filters (ticket_technical_assignees, ticket_cs_assignees,
 * ticket_sale_assignees) require integer employee IDs — FilterBuilder has no
 * async-combobox input type. Deferred until a custom FilterField extension or
 * a dedicated assignee-picker UI is available.
 */
const FILTER_FIELDS: FilterField[] = [
  { value: 'ticket_status', label: 'Status', input: 'select', options: TICKET_STATUS_OPTIONS },
  { value: 'ticket_priority', label: 'Priority', input: 'select', options: TICKET_PRIORITY_OPTIONS },
  { value: 'ticket_type_group', label: 'Type Group', input: 'select', options: TICKET_TYPE_GROUP_OPTIONS },
  { value: 'ticket_source', label: 'Source', input: 'select', options: TICKET_SOURCE_OPTIONS },
  { value: 'ticket_security_tag', label: 'Security Tag', input: 'select', options: TICKET_SECURITY_TAG_OPTIONS },
  { value: 'ticket_requested_at', label: 'Requested Date', input: 'date' },
  { value: 'ticket_response_late', label: 'Response SLA', input: 'select', options: TICKET_RESPONSE_LATE_OPTIONS },
  { value: 'ticket_marjorable', label: 'Major Incident', input: 'select', options: TICKET_MARJORABLE_OPTIONS },
  { value: 'product_group_name', label: 'Product Group', input: 'text' },
];

export default function TicketsPage() {
  const navigate = useNavigate();

  /* ── Tab ───────────────────────────────────────────────────────────────── */
  const [activeTab, setActiveTab] = useState<'list' | 'statistics'>('list');

  /* ── Statistics date range (outside tabs, drives all stat charts) ───────── */
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate]     = useState(defaultEndDate);

  const dateParams: StatDateParams = useMemo(
    () => ({ start_date: startDate, end_date: endDate }),
    [startDate, endDate],
  );

  /* ── Pagination ────────────────────────────────────────────────────────── */
  const [page, setPage] = useState(1);

  /* ── Search ────────────────────────────────────────────────────────────── */
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);

  /* ── Filters ───────────────────────────────────────────────────────────── */
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  /* ── Unassigned toggle ─────────────────────────────────────────────────── */
  const [showUnassigned, setShowUnassigned] = useState(false);

  /* ── Column visibility ─────────────────────────────────────────────────── */
  const [visible, setVisible] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      TICKET_COLUMNS.map((c) => [
        c.id as string,
        !TICKET_TOGGLEABLE_COLUMN_IDS.includes(c.id as string),
      ]),
    ),
  );

  /* ── Row selection (bulk-assign) ───────────────────────────────────────── */
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((k) => rowSelection[k]).map(Number),
    [rowSelection],
  );

  /* ── Export ────────────────────────────────────────────────────────────── */
  const [isExporting, setIsExporting] = useState(false);

  /* ── Build filter params from FilterCondition[] ────────────────────────── */
  const filterParams = useMemo(() => {
    const params: Record<string, unknown[]> = {};
    for (const cond of conditions) {
      if (!cond.value) continue;
      const values = Array.isArray(cond.value) ? cond.value : [cond.value];
      if (params[cond.field]) {
        params[cond.field] = [...params[cond.field], ...values];
      } else {
        params[cond.field] = values;
      }
    }
    return params;
  }, [conditions]);

  /* ── Query params ──────────────────────────────────────────────────────── */
  const listParams: TicketListParams = {
    page,
    page_size: PAGE_SIZE,
    sort_by: 'ticket_id',
    sort_order: 'desc',
    ...(query ? { q: query } : {}),
    ...filterParams,
  };

  /* ── Data fetch ────────────────────────────────────────────────────────── */
  const ticketsQuery    = useTickets(showUnassigned ? {} : listParams);
  const unassignedQuery = useUnassignedTickets(showUnassigned ? { page, page_size: PAGE_SIZE } : {});

  const activeQuery = showUnassigned ? unassignedQuery : ticketsQuery;
  const { data, isLoading, isError, refetch, isFetching } = activeQuery;

  const rows       = data?.results ?? [];
  const pagination = data?.pagination;
  const total      = pagination?.total ?? rows.length;
  const pageCount  = pagination?.total_pages ?? 1;

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  /* ── Reset page on query/filter change ─────────────────────────────────── */
  useEffect(() => { setPage(1); }, [query, conditions, showUnassigned]);

  /* ── Visible columns ────────────────────────────────────────────────────── */
  const visibleColumns = useMemo(
    () => TICKET_COLUMNS.filter((c) => visible[c.id as string] !== false),
    [visible],
  );

  /* ── Handlers ───────────────────────────────────────────────────────────── */
  const onSearch = (value: string) => { setRawQuery(value); setPage(1); };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== '' && c.value !== null));
    setPage(1);
    setFilterOpen(false);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportTickets(listParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date();
      a.href = url;
      a.download = `tickets-${toLocalDate(today)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRowClick = (row: (typeof rows)[number]) => {
    navigate(`/tickets/${row.ticket_id}`);
  };

  const handleRowSelectionChange = useCallback(
    (updater: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => {
      setRowSelection((prev) =>
        typeof updater === 'function' ? updater(prev) : updater,
      );
    },
    [],
  );

  return (
    <Container size="full">
      <PageHeader
        title="Tickets"
        description="Manage support tickets, incidents, and service requests."
        actions={
          <Button startIcon={<Ticket size={15} />} onClick={() => navigate('/tickets/new')}>
            New ticket
          </Button>
        }
      />

      {/* Date-range filter — outside tabs, drives the Statistics tab charts */}
      {activeTab === 'statistics' && (
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>
                Date range
              </span>
              <DateRangeInput
                aria-label="Date range"
                clearable={false}
                from={startDate}
                to={endDate}
                onChange={(from, to) => {
                  setStartDate(from);
                  setEndDate(to);
                }}
                style={{ width: 240 }}
              />
            </div>
          }
        />
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'list' | 'statistics')}>
        <TabsList style={{ marginBottom: 0 }}>
          <TabsTrigger value="list">List</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
        </TabsList>

        {/* ── List tab ─────────────────────────────────────────────────────── */}
        <TabsContent value="list">
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <TicketListToolbar
              columns={TICKET_COLUMNS}
              filterFields={FILTER_FIELDS}
              query={rawQuery}
              onQueryChange={onSearch}
              conditions={conditions}
              filterOpen={filterOpen}
              onFilterOpenChange={setFilterOpen}
              onApplyFilters={applyFilters}
              visible={visible}
              onToggleColumn={(id, checked) => setVisible((prev) => ({ ...prev, [id]: checked }))}
              showUnassigned={showUnassigned}
              onToggleUnassigned={(v) => { setShowUnassigned(v); setRowSelection({}); }}
              selectedIds={selectedIds}
              onBulkAssign={() => setBulkAssignOpen(true)}
              onExport={handleExport}
              isExporting={isExporting}
            />

            {isError ? (
              <Alert
                tone="danger"
                title="Failed to load tickets"
                actions={
                  <Button size="sm" variant="ghost" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isLoading ? (
              <Card flat>
                <CardBody>
                  <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                    {Array.from({ length: 10 }).map((_, i) => (
                      <Skeleton key={i} style={{ height: 24 }} />
                    ))}
                  </div>
                </CardBody>
              </Card>
            ) : (
              <DataTable
                columns={visibleColumns}
                data={rows}
                getRowId={(r) => String(r.ticket_id)}
                enableSelection
                rowSelection={rowSelection}
                onRowSelectionChange={handleRowSelectionChange}
                onRowClick={handleRowClick}
                emptyState={
                  <EmptyState
                    icon={<Ticket size={24} />}
                    title="No tickets found"
                    description="Try adjusting your search or filters."
                  />
                }
                footer={
                  <Pagination
                    page={page}
                    pageCount={view.pageCount}
                    onPageChange={setPage}
                    summary={`${view.summary}${isFetching ? ' …' : ''}`}
                  />
                }
              />
            )}
          </div>
        </TabsContent>

        {/* ── Statistics tab ───────────────────────────────────────────────── */}
        <TabsContent value="statistics">
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', paddingTop: 'var(--vn-space-4)' }}>
            <TicketStatisticsTab dateParams={dateParams} />
          </div>
        </TabsContent>
      </Tabs>

      <TicketBulkAssignDialog
        open={bulkAssignOpen}
        onOpenChange={setBulkAssignOpen}
        ticketIds={selectedIds}
        onSuccess={() => {
          setRowSelection({});
          setBulkAssignOpen(false);
        }}
      />
    </Container>
  );
}
